import AuthUserModelInterface from "../../../../kernel/shared/interfaces/auth-user-model.interface";
import { SidenavItemInterface } from "../interfaces/side-nav-items.interface";

/**
 * Runtime tree model for sidebar items.
 *
 * It wraps raw {@link SidenavItemInterface} definitions with hierarchy metadata,
 * active/expanded state, and route-matching helpers used by the side-nav UI.
 */
export class SidenavModel implements SidenavItemInterface {
  /** Normalized router link split into path pieces for fast prefix comparison. */
  private routerLinkArray: string[];

  id: string;
  isActive? = false;
  expanded? = false;
  tooltip?: string;
  routerLink?: string;
  label?: string;
  icon?: string;
  permissions?: string | (string | string[])[];
  items: SidenavModel[] = [];
  parent?: SidenavModel;
  isShown = true;
  direction?: string;
  routerLinkExact?: boolean;
  perform?: (item: SidenavItemInterface) => any;

  /**
   * Creates a navigation node and recursively materializes child nodes.
   *
   * @param item Raw sidenav item configuration.
   * @param parent Parent node in the sidenav tree.
   */
  constructor(item: SidenavItemInterface, parent?: SidenavModel) {
    this.id = item.id;
    this.expanded = item.expanded;
    this.isActive = item.isActive;
    this.icon = item.icon;
    this.label = item.label;
    this.routerLink = item.routerLink;
    this.routerLinkArray = this.initRouterLinkArray(item.routerLink ?? "/");
    this.tooltip = item.tooltip;
    this.permissions = item.permissions;
    this.parent = parent;
    this.direction = item.direction;
    this.routerLinkExact = item.routerLinkExact;
    this.perform = item.perform;
    item.items?.forEach((item: SidenavItemInterface) => this.items.push(new SidenavModel(item, this)));
  }

  /**
   * Replaces current node children with a new set of top-level links.
   *
   * @param mainLinks Links to materialize as children.
   * @returns The created child nodes.
   */
  setMainLinks(mainLinks: SidenavItemInterface[]): SidenavModel[] {
    this.items = mainLinks.map((link: SidenavItemInterface) => new SidenavModel(link, this));
    return this.items;
  }

  /**
   * Appends child links under the current node.
   *
   * @param subItems Links to append.
   * @returns The current node for chaining.
   */
  setSubMainLinks(subItems: SidenavItemInterface[]): SidenavModel {
    this.items = this.items.concat(subItems.map(item => new SidenavModel(item, this)));
    return this;
  }

  /**
   * Expands the current item and propagates active/expanded state to ancestors.
   *
   * @param activateParent When true, mark ancestors as active even if they are not routable.
   */
  expandItem(activateParent = false): void {
    if (this.routerLink || activateParent) {
      this.isActive = true;
    }
    if (this.items.length) {
      this.expanded = true;
    }
    if (this.parent) {
      this.parent.expandItem(activateParent);
    }
  }

  /** Clears expanded and active state recursively for this node and descendants. */
  unExpandAllItems(): void {
    this.expanded = false;
    this.isActive = false;
    this.items.forEach(item => item.unExpandAllItems());
  }

  /**
   * Collapses this node and its descendants while preserving active state.
   *
   * Toggling a group only changes what is visible, so the branch holding the
   * current route must stay marked as active.
   */
  collapseAllItems(): void {
    this.expanded = false;
    this.items.forEach(item => item.collapseAllItems());
  }

  /**
   * Evaluates visibility of this node and descendants against user permissions.
   *
   * Parent nodes become visible when at least one child remains visible.
   *
   * @param user Current authenticated user.
   * @returns The current node for chaining.
   */
  checkPermissions(user: AuthUserModelInterface | null | undefined): SidenavModel {
    if (!this.permissions || !this.permissions?.length) {
      this.isShown = true;
    } else {
      this.isShown = user ? user.can(this.permissions) : false;
    }
    for (const item of this.items ?? []) {
      item.checkPermissions(user);
    }
    this.isShown = this.items.length ? this.items.some((i: any) => i.isShown) : this.isShown;
    return this;
  }

  /**
   * Traverses descendants and activates the item with the strongest route match.
   *
   * @param routerLink Current route split into path pieces.
   * @param minRate Current best match ratio while traversing.
   * @param parent Parent used to clear previous active branch when a better match is found.
   * @returns Best match ratio found in the current subtree.
   */
  activateItem(routerLink: string[], minRate = 0, parent?: SidenavModel): number {
    for (const item of this.items) {
      const matchedRate = item.getMatchRate(routerLink);

      if (matchedRate > minRate && item.isShown) {
        parent?.unExpandAllItems();
        item.expandItem(true);
        minRate = matchedRate;
        if (minRate === 1) {
          return minRate;
        }
      }

      if (item.items.length) {
        minRate = item.activateItem(routerLink, minRate, this);
        if (minRate === 1) {
          return minRate;
        }
      }
    }

    return minRate;
  }

  /**
   * Returns the matched prefix pieces when current node route is a prefix of the target route.
   *
   * @param routerLink Target route split into path pieces.
   * @returns Matching prefix pieces or an empty array when mismatch occurs.
   */
  extractMatchedRoutePieces(routerLink: string[]) {
    let index = 0;
    for (const piece of this.routerLinkArray) {
      if (routerLink[index++] !== piece) {
        return [];
      }
    }
    return this.routerLinkArray;
  }

  /**
   * Computes match ratio between this node route and the target route.
   *
   * @param routerLink Target route split into path pieces.
   * @returns Match ratio in range [0, 1].
   */
  getMatchRate(routerLink: string[]) {
    return this.extractMatchedRoutePieces(routerLink).length / routerLink.length;
  }

  /**
   * Normalizes a route and converts it to comparable path pieces.
   *
   * The route is forced to start with `/` and trailing slash is removed
   * (except for root `/`) to avoid false mismatches.
   *
   * @param route Route string from item configuration.
   * @returns Route pieces used by matching helpers.
   */
  private initRouterLinkArray(route: string) {
    route = route.startsWith("/") ? route : "/" + route;

    // Normalize optional trailing slash so parent routes still match nested urls.
    while (route.length > 1 && route.endsWith("/")) {
      route = route.slice(0, -1);
    }
    return route.split("/");
  }
}
